import { BROKER_SYNC_PROVIDER_LABELS } from "@signals/shared";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "../_common";
import type { ProviderSetupProps } from "./broker-sync-panel.types";
import { localeKey, t } from "./broker-sync-panel-format.util";
import { ProviderConnectionResult } from "./broker-sync-connection-result";

export function ProviderSetup({
  provider,
  tokenStatuses,
  secretValues,
  secretInputsKey,
  instructions,
  managementCode,
  connectionResult,
  connectionError,
  checking,
  secrets,
  onCheck,
}: ProviderSetupProps) {
  const providerLabel = BROKER_SYNC_PROVIDER_LABELS[provider][localeKey()];
  const hasUnsavedSecret = secretValues.some((item) => item.hasValue);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{providerLabel}</CardTitle>
        <CardDescription>{instructions}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{t("portfolio.brokerSync.instructions")}</h4>
          <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.accountIdHint")}</p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {tokenStatuses.map((item) => (
            <div key={item.label} className="rounded-md border p-3 text-sm">
              <div className="font-medium">{item.label}</div>
              <div className="mt-1 text-muted-foreground">
                {item.isSet === null
                  ? t("portfolio.brokerSync.secretUnknown")
                  : item.isSet
                    ? t("portfolio.brokerSync.secretSaved")
                    : t("portfolio.brokerSync.secretMissing")}
              </div>
            </div>
          ))}
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {secretValues.map((item) => (
            <label key={`${item.label}:${secretInputsKey}`} className="block space-y-1 text-sm font-medium">
              <span>{item.label}</span>
              {/* Uncontrolled on purpose: a controlled input mirrors its value into the DOM `value`
                  attribute, which would leave an unsaved secret readable in the page markup. */}
              <input
                type="password"
                autoComplete="new-password"
                defaultValue=""
                onChange={(event) => item.onChange(event.target.value)}
                placeholder={t("portfolio.brokerSync.replaceSecret")}
                className="h-10 w-full rounded-md border bg-background px-3"
              />
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{t("portfolio.brokerSync.secretInputHint")}</p>
        <div className="space-y-3">
          <Button type="button" variant="outline" disabled={!managementCode || checking} onClick={() => onCheck(provider)}>
            {checking ? t("portfolio.brokerSync.checkingConnection") : t("portfolio.brokerSync.checkConnection")}
          </Button>
          {hasUnsavedSecret ? (
            <p className="text-xs text-amber-700">{t("portfolio.brokerSync.saveTokenFirst")}</p>
          ) : null}
          {connectionError ? <p role="alert" className="text-sm text-destructive">{connectionError}</p> : null}
          {connectionResult ? <ProviderConnectionResult result={connectionResult} secrets={secrets} /> : null}
        </div>
      </CardContent>
    </Card>
  );
}
